import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stack,
} from '@mui/material';
import { useMemo } from 'react';
import {
  Form, RecordContextProvider, useNotify, useTranslate, useRefresh,
  required,
} from 'react-admin';
import { PointGeometry, SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import { GrayTextInputWithUpperLabel } from '../../../components/inputs/GrayTextInputWithUpperLabel';
import { GrayGoogleCoordinatesInputWithUpperLabel } from '../../../components/inputs/GrayGoogleCoordinatesInputWithUpperLabel';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { securityItemsResource } from '../util';
import { GrayNumberInputWithUpperLabel } from '../../../components/inputs/GrayNumberInputWithUpperLabel';
import { ExpiryDateDisplay } from '../../../components/ExpiryDateDisplay';

export const EditItemModal = (props: {
  record: SecurityInventoryItem | null;
  categories: SecurityInventoryCategory[];
  open: boolean;
  onClose: () => void;
}) => {

  const record = props.record;

  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();
  const category = useMemo(() => {

    return props.categories.find((c) => c._id === record?.category?._id) ?? null;

  }, [props.categories, record]);

  if (!record) {

    return null;

  }

  const handleSubmit = async (values: Record<string, unknown>) => {

    const previousData = record;
    if (!previousData) {

      return;

    }

    const base = values.location as PointGeometry | null;
    const floor = (values as SecurityInventoryItem).location?.properties?.address?.floor;

    const lastInspectionDate = new Date(values.lastInspectionDate as string).toISOString();
    const expiryDate = new Date(values.expiryDate as string).toISOString();

    const data = {
      name: values.name,
      lastInspectionDate,
      expiryDate,
      location: {
        ...base,
        properties: {
          ...base?.properties,
          address: {
            ...base?.properties?.address,
            ...(floor !== undefined && floor !== null ? { floor } : {}),
          },
        },
      },
    };
    try {

      await xgacDataProvider.update(securityItemsResource, {
        id: previousData._id,
        data,
        previousData,
      });
      refresh();
      props.onClose();

      notify('ra.notification.updated', {
        messageArgs: { smart_count: 1 },
        type: 'success',
      });

    } catch {

      notify('ra.notification.http_error', { type: 'warning' });

    }

  };

  return (
    // disablePortal in this case allows the location input to appear on top of the dialog and not be covered by the dialog
    // onClose is set to prevent the dialog from being closed when a click outside of the dialog occurs
    <Dialog
      open={props.open}
      onClose={(_event, reason) => {

        if (reason === 'backdropClick') {

          return;

        }
        props.onClose();

      }}
      disablePortal
      sx={{
        '& .MuiDialog-paper': {
          width: '800px',
          maxWidth: '100%',
          maxHeight: '600px',
        },
      }}>
      <DialogTitle>{translate('resources.security-inventory-items.text.edit_item_title')}</DialogTitle>
      <DialogContent>
        <RecordContextProvider value={record}>
          <Form
            id="edit-item-form"
            record={record}
            onSubmit={handleSubmit}
          >
            <Stack spacing={0}>
              <GrayTextInputWithUpperLabel
                source="name"
                label="Name"
                validate={required()}
              />
              <GrayGoogleCoordinatesInputWithUpperLabel
                source="location"
                label={translate('general.text.location')}
                placeholder={translate('resources.buttons.text.building_dialog.0.input_label')}
                validate={required()}
              />
              <ExpiryDateDisplay
                startDateSource="lastInspectionDate"
                expiryDateSource="expiryDate"
                timeGranularity={category?.expiryInterval}
                startDateLabel="resources.security-inventory-items.fields.last_inspection_date"
                expiryDateLabel="resources.security-inventory-items.fields.expiry_date"
                helperText={translate('resources.security-inventory-items.text.expiry_date_input_helper_text')}
              />
              <GrayNumberInputWithUpperLabel
                source="location.properties.address.floor"
                label={translate('general.text.map_floors')}
                size="medium"
                validate={required()}
              />
            </Stack>
          </Form>
        </RecordContextProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>{translate('ra.action.cancel')}</Button>
        <Button type="submit" form="edit-item-form">{translate('ra.action.save')}</Button>
      </DialogActions>
    </Dialog>
  );

};
